import TableOnly from "components/DataTable/TableOnly";
import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";
import Pagination from "components/Pagination/Pagination";
import SearchComp from "components/SearchComp/SearchComp";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import { ICONS } from "./workflowGraph";

/**
 * Sales -> Utilities -> Products -> Stations (user, 2026-10-29). Print Film,
 * Screen Burning, Shirt Receiving ... Every workflow step picks one of these,
 * so later each station can show the work waiting for it. A station still
 * used by a step cannot be deleted.
 */

const COMPONENT_URL = "/sales/products";
const limits = [5, 10, 15, 20];

export default function SStations() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [stations, setStations] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [formData, setFormData] = useState({});

  const notify = (message, type = "success") => dispatch(setNotification({ message, type }));

  const changeLimit = (val) =>
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("limit", val);
      newParams.delete("page");
      return newParams.toString();
    });

  const getSearchParamsWithoutGivenKey = (key) => {
    const params = new URLSearchParams(searchParams);
    params.delete(key);
    return params.toString();
  };

  const getStations = async () => {
    try {
      setLoading(true);
      const body = {
        page: parseInt(searchParams.get("page")) || 1,
        limit: parseInt(searchParams.get("limit")) || 10,
        query: searchParams.get("query") || "",
      };
      const { data } = await API.post("/sales/stations", body, { headers: authHeader() });
      if (!data?.success) throw new Error(data?.message || "Failed");
      setStations(data.data || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      notify(err?.response?.data?.message || err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(getStations, 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Runs a change, reports it, reloads the list.
  const run = async (fn) => {
    try {
      setLoading(true);
      const { data } = await fn();
      if (data?.success === false) throw new Error(data?.message || "Failed");
      if (data?.message) notify(data.message);
      await getStations();
      return data;
    } catch (err) {
      notify(err?.response?.data?.message || err.message, "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = { name: formData.name, icon: formData.icon, description: formData.description };
    const data = await run(() =>
      isUpdateMode
        ? API.patch("/sales/station", { ...body, id: formData.id }, { headers: authHeader() })
        : API.post("/sales/station", body, { headers: authHeader() }),
    );
    if (data) setShowAddModal(false);
  };

  const table_head = [
    { name: "ID", value: "id" },
    { name: "Station", value: "name" },
    { name: "Description", value: "description" },
    { name: "Used in Steps", value: "steps" },
    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item) => (
    <tr key={item.id}>
      <td className="py-0 align-middle">{item.id}</td>
      <td className="py-0 align-middle">
        <i className={`mdi mdi-${item.icon || "cog-outline"} me-2 text-primary`} />
        {item.name}
      </td>
      <td className="py-0 align-middle">{item.description}</td>
      <td className="py-0 align-middle">{item.steps}</td>
      <td className="py-0 align-middle table-action text-nowrap">
        <button
          onClick={() => run(() => API.post("/sales/station/move", { id: item.id, direction: "up" }, { headers: authHeader() }))}
          className="action-icon border-0 btn"
        >
          <i className="mdi mdi-chevron-double-up"></i>
        </button>
        <button
          onClick={() => run(() => API.post("/sales/station/move", { id: item.id, direction: "down" }, { headers: authHeader() }))}
          className="action-icon border-0 btn"
        >
          <i className="mdi mdi-chevron-double-down"></i>
        </button>
        <button
          disabled={loading}
          onClick={() => {
            setFormData({ id: item.id, name: item.name, icon: item.icon, description: item.description || "" });
            setIsUpdateMode(true);
            setShowAddModal(true);
          }}
          className="btn px-1"
        >
          <i className="mdi mdi-pencil"></i>
        </button>
        <DeleteModalButton
          btnClass={"btn  px-1"}
          deleteFunc={(id) => run(() => API.delete(`/sales/station/${id}`, { headers: authHeader() }))}
          deleteParams={item.id}
          disableConditions={loading}
        />
      </td>
    </tr>
  );

  return (
    <div className="p-2" style={{ height: "90vh" }}>
      {loading && <Spinner />}
      <div className="px-2">
        <div className="d-flex justify-content-end align-items-center gap-1 mb-3">
          <div className="flex-grow-1 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-1">
              <div>Show</div>
              <button
                className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {searchParams.get("limit") || 10}
              </button>
              <ul style={{ width: "fit-content" }} className="dropdown-menu">
                {limits.map((limit) => (
                  <li key={limit}>
                    <button className="dropdown-item" onClick={() => changeLimit(limit)}>
                      {limit}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <SearchComp
            setLoading={setLoading}
            apiPath={`${COMPONENT_URL}${
              getSearchParamsWithoutGivenKey("query") ? "?" + getSearchParamsWithoutGivenKey("query") + "&" : "?"
            }`}
          />
          <div>
            <button
              onClick={() => {
                setFormData({ name: "", icon: "cog-outline", description: "" });
                setIsUpdateMode(false);
                setShowAddModal(true);
              }}
              type="button"
              className="btn btn-primary ms-1"
            >
              Add Station
            </button>
          </div>
        </div>
        <div className="mb-3" style={{ height: "75vh", overflowY: "auto", borderRadius: 15 }}>
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={stations}
            totalPages={totalPages}
            disableSorting
          />
        </div>
        <Pagination
          totalPages={totalPages}
          apiPath={`${COMPONENT_URL}${
            searchParams.toString() ? "?" + getSearchParamsWithoutGivenKey("page") + "&" : "?"
          }page=`}
          currPage={parseInt(searchParams.get("page")) || 1}
        />
      </div>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Body className="shadow-lg bg-white px-3 pb-4" style={{ borderRadius: 15 }}>
          <form onSubmit={submit}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h4>{isUpdateMode ? "Update Station" : "Add Station"}</h4>
              <div className="d-flex justify-content-end">
                <button onClick={() => setShowAddModal(false)} className="btn btn-secondary btn-sm" type="button">
                  Close
                </button>
                <button
                  disabled={loading || !String(formData.name || "").trim()}
                  className="btn btn-primary btn-sm ms-1"
                  type="submit"
                >
                  {isUpdateMode ? "Update" : "Add"}
                </button>
              </div>
            </div>
            <hr className="mt-0" />
            <div className="my-3">
              <label className="form-label">Station Name</label>
              <input
                className="form-control"
                autoFocus
                required
                maxLength={255}
                value={formData.name || ""}
                onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Print Film"
              />
            </div>
            <div className="my-3">
              <label className="form-label">Description</label>
              <input
                className="form-control"
                maxLength={255}
                value={formData.description || ""}
                onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
                placeholder="e.g. Waiting for film preparation"
              />
            </div>
            <div className="my-3">
              <label className="form-label">Icon</label>
              <div className="d-flex flex-wrap gap-1">
                {ICONS.map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    className={`btn ${formData.icon === icon ? "btn-primary" : "bg-white border"}`}
                    onClick={() => setFormData((f) => ({ ...f, icon }))}
                  >
                    <i className={`mdi mdi-${icon}`} />
                  </button>
                ))}
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
